import { memo, useEffect, useRef, useState } from "react";
import { ScanBarcode } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import type { RentalStockItem } from "../../types/rental.types";

interface PickupStockScannerProps {
  items: RentalStockItem[];
  onMatch: (stockId: number) => void;
}

/**
 * Input de leitor de barcode da retirada SUIT.
 *
 * Resolve localmente — `RentalStockItem` já traz o barcode pela API,
 * então não há fetch nenhum: basta procurar o código no array. Aceita
 * re-bipar uma peça já marcada (responsabilidade do pai decidir o que
 * fazer). Auto-foca no input ao montar para fluxo "bipa-bipa".
 */
export const PickupStockScanner = memo(function PickupStockScanner({
  items,
  onMatch,
}: PickupStockScannerProps) {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const code = value.trim();
    if (code === "") return;

    const match = items.find((item) => item.barcode === code);
    if (!match) {
      setError("Peça não pertence a este aluguel.");
      return;
    }

    onMatch(match.id);
    setValue("");
    setError(null);
    inputRef.current?.focus();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <Label htmlFor="pickup-barcode-input">Bipar peça</Label>
      <div className="relative">
        <ScanBarcode className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          id="pickup-barcode-input"
          ref={inputRef}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            if (error) setError(null);
          }}
          placeholder="Escaneie ou digite o código e tecle Enter"
          className="pl-8"
          aria-describedby={error ? "pickup-barcode-error" : undefined}
        />
      </div>
      {error ? (
        <p id="pickup-barcode-error" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </form>
  );
});
